import { useState } from 'react';
import {
  ActionButton,
  Button,
  DataTable,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  HairlineCard,
  Icons,
  MenuItem,
  RowAction,
  RowActions,
  Stack,
  TagChip,
  TextField,
  titleCellSx,
  Typography,
  useRowActionsColumn,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { InviteLanguageField, type InviteLanguage } from './InviteLanguageField';
import { COMPANY_ROLES } from '@munifocus/access';
import {
  useAddCompanyMember,
  useCompanies,
  useCompanyMembers,
  useUpdateCompanyMember,
} from '../../lib/administration-api';
import { useScope } from '../../providers/ScopeProvider';
import { useMutationToast } from '../../lib/mutation-toast';
import { useChangeSeat, useCompanySeats } from '../../lib/seats-api';
import { SeatCount } from './SeatsPanel';

/**
 * A contractor's own workforce — the "manage employees" half of the brief.
 *
 * Employment is not enforcement authority: an employee added here can do
 * nothing operational until council appoints them at a municipality the company
 * holds a live contract with. The "appointed at" column is where that shows,
 * and an employee with an empty one is exactly the person who cannot yet work.
 */
export function EmployeesPanel() {
  const { t } = useTranslation();
  const actionsColumn = useRowActionsColumn();
  const toast = useMutationToast();
  const { active, can } = useScope();

  // Company admins usually work in their own company scope; a Munifocus
  // operator arriving from the platform picks one.
  const companies = useCompanies(active?.kind !== 'company');
  const companyId = active?.kind === 'company' ? active.id : (companies.data?.[0]?.id ?? null);

  const members = useCompanyMembers(companyId);
  const add = useAddCompanyMember(companyId ?? '');
  const update = useUpdateCompanyMember(companyId ?? '');
  const seats = useCompanySeats(companyId);
  const changeSeat = useChangeSeat(companyId ?? '');

  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<string>('company_officer');
  const [title, setTitle] = useState('');
  const [language, setLanguage] = useState<InviteLanguage>('en');

  /**
   * Resolves whether the action landed, so a dialog can stay open — with what
   * was typed still in it — when it did not. Closing and clearing after a failed
   * save threw the officer's input away behind an error toast.
   */
  const run = async (successKey: string, fn: () => Promise<unknown>): Promise<boolean> => {
    try {
      await fn();
      toast.success(successKey);
      return true;
    } catch (err) {
      toast.error(err, 'gov.admin.errors.actionFailed');
      return false;
    }
  };

  if (!companyId) {
    return (
      <HairlineCard sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('gov.admin.employees.noCompany')}
        </Typography>
      </HairlineCard>
    );
  }

  const rows = members.data ?? [];

  return (
    <Stack spacing={2}>
      {seats.data && (
        <HairlineCard sx={{ p: 2 }}>
          <Typography variant="body2">
            <SeatCount used={seats.data.used} limit={seats.data.limit} />
            {' · '}
            {t('gov.admin.employees.seatsHint')}
          </Typography>
        </HairlineCard>
      )}
      <DataTable
        rows={rows}
        getRowKey={(row) => row.id}
        isLoading={members.isLoading}
        emptyLabel={t('gov.admin.employees.empty')}
        pageSize={25}
        initialSortKey="name"
        search={{
          placeholder: t('gov.admin.employees.searchPlaceholder'),
          of: (row) => `${row.userName} ${row.userEmail} ${row.title}`,
        }}
        // A deactivated employee stays on the roster, dimmed: the record of who
        // worked here has to outlive their access.
        rowSx={(row) => ({ opacity: row.isActive ? 1 : 0.55 })}
        action={
          can('employee:manage') ? (
            <ActionButton
              label={t('gov.admin.employees.add')}
              icon={Icons.Plus}
              onClick={() => setAdding(true)}
            />
          ) : null
        }
        columns={[
          {
            key: 'name',
            label: t('gov.admin.employees.name'),
            primary: true,
            sortValue: (row) => row.userName,
            cell: (row) => (
              <Stack>
                <Typography variant="body2" sx={titleCellSx}>
                  {row.userName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.userEmail}
                </Typography>
              </Stack>
            ),
          },
          {
            key: 'role',
            label: t('gov.admin.employees.role'),
            sortValue: (row) => row.role,
            cell: (row) => <TagChip label={t(`gov.admin.roles.${row.role}`)} />,
          },
          {
            key: 'title',
            label: t('gov.admin.employees.title'),
            sortValue: (row) => row.title,
            cell: (row) => row.title || '—',
          },
          {
            key: 'seat',
            label: t('gov.admin.employees.seat'),
            sortValue: (row) => (row.hasSeat ? 1 : 0),
            cell: (row) =>
              row.hasSeat ? (
                <TagChip label={t('gov.admin.employees.hasSeat')} />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t('gov.admin.employees.noSeat')}
                </Typography>
              ),
          },
          {
            key: 'appointedAt',
            label: t('gov.admin.employees.appointedAt'),
            sortValue: (row) => row.appointedAt.length,
            cell: (row) =>
              row.appointedAt.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {t('gov.admin.employees.notAppointed')}
                </Typography>
              ) : (
                <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                  {row.appointedAt.map((place) => (
                    <TagChip key={place.localGovernmentId} label={place.name} />
                  ))}
                </Stack>
              ),
          },
          ...(can('employee:manage')
            ? [
                {
                  ...actionsColumn,
                  cell: (row: (typeof rows)[number]) => (
                    <RowActions>
                      {row.isActive && (
                        <RowAction
                          label={
                            row.hasSeat ? t('gov.admin.seats.remove') : t('gov.admin.seats.assign')
                          }
                          icon={row.hasSeat ? Icons.UserMinus : Icons.BadgeCheck}
                          tone={row.hasSeat ? 'danger' : 'default'}
                          onClick={() =>
                            run(
                              row.hasSeat ? 'gov.admin.seats.removed' : 'gov.admin.seats.assigned',
                              () =>
                                changeSeat.mutateAsync({
                                  action: row.hasSeat ? 'remove' : 'assign',
                                  membershipId: row.id,
                                }),
                            )
                          }
                        />
                      )}
                      <RowAction
                        label={
                          row.isActive
                            ? t('gov.admin.employees.deactivate')
                            : t('gov.admin.employees.reactivate')
                        }
                        icon={row.isActive ? Icons.UserMinus : Icons.UserCheck}
                        tone={row.isActive ? 'danger' : 'default'}
                        onClick={() =>
                          run(
                            row.isActive
                              ? 'gov.admin.employees.deactivated'
                              : 'gov.admin.employees.reactivated',
                            () => update.mutateAsync({ id: row.id, isActive: !row.isActive }),
                          )
                        }
                      />
                    </RowActions>
                  ),
                },
              ]
            : []),
        ]}
      />

      <Dialog open={adding} onClose={() => setAdding(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('gov.admin.employees.add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={t('gov.admin.employees.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              type="email"
            />
            <TextField
              label={t('gov.admin.employees.name')}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              select
              label={t('gov.admin.employees.role')}
              value={role}
              onChange={(e) => setRole(e.target.value)}
              fullWidth
            >
              {COMPANY_ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {t(`gov.admin.roles.${r}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('gov.admin.employees.title')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
            />
            <InviteLanguageField value={language} onChange={setLanguage} />
            <Typography variant="caption" color="text.secondary">
              {t('gov.admin.employees.addHelp')}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!email || !displayName || add.isPending}
            onClick={async () => {
              const ok = await run('gov.admin.employees.added', () =>
                add.mutateAsync({ email, displayName, role: role as never, title, language }),
              );
              if (!ok) return;
              setAdding(false);
              setEmail('');
              setDisplayName('');
              setTitle('');
              setLanguage('en');
            }}
          >
            {t('common.actions.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
